import React, { useCallback } from "react";
import Formsy from "formsy-react";
import { useFormRef } from "./Form";
import getNestedValue from "lodash/get";
import setNestedValue from "lodash/set";
import merge from "lodash/merge";

/** @type {import('react').FunctionComponent} */
const FormComponent = React.forwardRef((props, ref) => {
  return <Formsy {...props} ref={ref} />;
});

export default FormComponent;

export const useFormFieldState = (fieldName, initialValue) => {
  const form = useFormRef().current;
  const value = getNestedValue(form?.getModel(), fieldName) ?? initialValue;
  const setValue = useCallback(
    (value) => {
      const formData = form?.getModel();
      const oldValue = getNestedValue(formData, fieldName);
      const newValue = typeof value === "function" ? value(oldValue) : value;
      if (newValue !== oldValue) {
        form.updateInputsWithValue(
          setNestedValue(merge({}, formData), fieldName, newValue),
          true
        );
      }
    },
    [form, fieldName]
  );
  return [value, setValue];
};

/**
 * @callback FormDataSetter
 * @param {Object | Function} value - the value to set the form object to, or a function to get the value (note this function will be sent the old form value)
 * @param {{ doMerge: Boolean }} options - use doMerge to merge value with old form value instead of replace
 */

/**
 * @returns {FormDataSetter}
 */
export const useFormDataSetter = () => {
  const form = useFormRef().current;
  const setFormData = useCallback(
    (value, { doMerge = false } = {}) => {
      const oldValue = form.getModel();
      const newValue = typeof value === "function" ? value(oldValue) : value;
      form.updateInputsWithValue(
        doMerge ? merge({}, oldValue, newValue) : newValue,
        true
      );
    },
    [form]
  );
  return setFormData;
};

export const useFormErrorSetter = () => {
  const form = useFormRef().current;
  const setFormErrors = useCallback(
    (errors) => {
      form && form.updateInputsWithError(errors, true);
    },
    [form]
  );
  return setFormErrors;
};
